import type { AxiosInstance } from 'axios';
import {
  SUCCESS_CODE,
  WorkpayApiError,
  isUnauthorizedError,
  isSessionHostMismatchError,
} from './apiEnvelope';
import type { ApiEnvelope } from './apiEnvelope';

// Allow an opt-in `successToast` flag on any axios request config. Set in the
// service layer on mutation endpoints; consumed by the interceptor below.
declare module 'axios' {
  interface AxiosRequestConfig {
    /** When true, a successful response invokes `onSuccessToast` with status.description. */
    successToast?: boolean;
  }
}

export interface EnvelopeInterceptorOptions {
  /** Invoked with status.description when a successful request set `config.successToast`. */
  onSuccessToast?: (description: string) => void;
  /** Invoked when an unauthorized / session-gone WorkpayApiError is produced. */
  onUnauthorized?: (error: WorkpayApiError) => void;
  /** Invoked when a session host-binding mismatch (48015) WorkpayApiError is produced. */
  onSessionHostMismatch?: (error: WorkpayApiError) => void;
}

/**
 * Applies the Workpay uniform-envelope response interceptor to an axios client.
 *
 * - Success (`status.code === SUCCESS_CODE`): unwraps `response.data` to the
 *   payload, so callers read `response.data` directly. Fires `onSuccessToast`
 *   when the request opted in via `config.successToast`.
 * - Non-success (HTTP 200 business error or non-200 gateway error): rejects a
 *   normalised `WorkpayApiError`. Unauthorized-family outcomes invoke
 *   `onUnauthorized`.
 * - Binary responses (blob/arraybuffer/stream) bypass the envelope.
 *
 * App-specific side-effects (success toast, session-expiry redirect) are
 * injected via `options` so the same interceptor serves every app.
 */
export function applyEnvelopeInterceptor(
  client: AxiosInstance,
  options: EnvelopeInterceptorOptions = {},
) {
  client.interceptors.response.use(
    (response) => {
      const rt = response.config.responseType;
      if (rt === 'blob' || rt === 'arraybuffer' || rt === 'stream') return response;

      const body = response.data as ApiEnvelope<unknown> | undefined;
      if (typeof body?.status?.code !== 'number') return response; // not enveloped — pass through

      if (body.status.code === SUCCESS_CODE) {
        if (response.config.successToast) options.onSuccessToast?.(body.status.description);
        response.data = body.data;
        return response;
      }

      const err = new WorkpayApiError(
        body.status.code,
        body.status.description,
        body.status.fields,
        200,
      );
      if (isUnauthorizedError(err)) options.onUnauthorized?.(err);
      if (isSessionHostMismatchError(err)) options.onSessionHostMismatch?.(err);
      return Promise.reject(err);
    },
    (error) => {
      const body = error.response?.data as ApiEnvelope<unknown> | undefined;
      const err =
        typeof body?.status?.code === 'number'
          ? new WorkpayApiError(
              body.status.code,
              body.status.description,
              body.status.fields,
              error.response?.status ?? 0,
            )
          : new WorkpayApiError(0, 'K_NETWORK_ERROR', undefined, error.response?.status ?? 0);
      if (isUnauthorizedError(err)) options.onUnauthorized?.(err);
      if (isSessionHostMismatchError(err)) options.onSessionHostMismatch?.(err);
      return Promise.reject(err);
    },
  );
}
